// src/app/time-attendance/components/offtime/services/offtime.service.ts

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { expand, map, reduce } from 'rxjs/operators';

import { environment } from '../../../environments/environment';

import {
  AddMoreDayOffRequestsBody,
  ApiPagedResponse,
  ApiResponse,
  ApprovalFilter,
  CreateDayOffRequestBody,
  CurrentUserDto,
  DayOffGroupQuery,
  OfftimeGroupScope,
  OfftimePayCodeDto,
  OfftimeRequestGroupDetailsDto,
  OfftimeRequestGroupListDto,
  OfftimeUserListDto,
  PatchDayOffRequestBody,
} from '../models/offtime.model';

@Injectable({ providedIn: 'root' })
export class OfftimeService {
  private readonly baseUrl = environment.apiBaseUrl;

  private readonly myGroupsUrl = `${this.baseUrl}/api/user-transactions/user-day-off-request-groups`;
  private readonly allGroupsUrl = `${this.baseUrl}/api/day-off-request-groups`;

  constructor(private readonly http: HttpClient) { }

  getLocalTimeZoneId(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return 'UTC';
    }
  }

  private buildParams(paramsObj: Record<string, unknown>): HttpParams {
    let params = new HttpParams();

    Object.entries(paramsObj).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });

    return params;
  }

  private toIsApprovedParam(filter: ApprovalFilter | undefined): 'true' | 'false' | undefined {
    if (filter === true) return 'true';
    if (filter === false) return 'false';
    return undefined;
  }

  private resolveGroupsBaseUrl(scope: OfftimeGroupScope | undefined): string {
    return scope === 'all' ? this.allGroupsUrl : this.myGroupsUrl;
  }

  private fetchAllUsers(baseParams?: HttpParams): Observable<OfftimeUserListDto[]> {
    const pageSize = 100;
    const initialParams = (baseParams ?? new HttpParams())
      .set('pageNumber', 1)
      .set('pageSize', pageSize);

    return this.http
      .get<ApiPagedResponse<OfftimeUserListDto[]>>(`${this.baseUrl}/api/users`, {
        params: initialParams,
      })
      .pipe(
        expand((res) => {
          if (res.currentPage >= res.totalPages) {
            return of();
          }

          const nextParams = (baseParams ?? new HttpParams())
            .set('pageNumber', res.currentPage + 1)
            .set('pageSize', pageSize);

          return this.http.get<ApiPagedResponse<OfftimeUserListDto[]>>(`${this.baseUrl}/api/users`, {
            params: nextParams,
          });
        }),
        map((res) => res.data ?? []),
        reduce((all, page) => [...all, ...page], [] as OfftimeUserListDto[]),
      );
  }

  getPayCodes(): Observable<OfftimePayCodeDto[]> {
    return this.http
      .get<ApiResponse<OfftimePayCodeDto[]>>(
        `${this.baseUrl}/api/day-off-requests/get-pay-codes`
      )
      .pipe(
        map((response) =>
          (response.data ?? []).filter(
            (payCode) => payCode.isDayOff && payCode.isActive
          )
        )
      );
  }

  getUsers(search?: string): Observable<OfftimeUserListDto[]> {
    let params = new HttpParams().set('employmentStatus', 'Active');

    if (search?.trim()) {
      params = params.set('search', search.trim());
    }

    return this.fetchAllUsers(params);
  }

  getActiveUsers(search?: string): Observable<OfftimeUserListDto[]> {
    let params = new HttpParams().set('employmentStatus', 'Active');

    if (search?.trim()) {
      params = params.set('search', search.trim());
    }

    return this.fetchAllUsers(params);
  }

  getCurrentUser(): Observable<CurrentUserDto> {
    return this.http.get<CurrentUserDto>(`${this.baseUrl}/api/account/get-current-user`);
  }

  getDayOffRequestGroups(
    options?: DayOffGroupQuery,
  ): Observable<ApiPagedResponse<OfftimeRequestGroupListDto[]>> {
    const scope = options?.scope ?? 'mine';
    const isCanceled = options?.isCanceled === true;
    const isApprovedParam = this.toIsApprovedParam(options?.isApproved);

    const params = this.buildParams({
      pageNumber: options?.pageNumber ?? options?.page,
      pageSize: options?.pageSize,
      isCanceled: isCanceled ? 'true' : undefined,
      isApproved: isApprovedParam,
      orderBy: options?.orderBy,
      startDate: options?.startDate,
      endDate: options?.endDate,
    });

    const url = this.resolveGroupsBaseUrl(scope);

    return this.http.get<ApiPagedResponse<OfftimeRequestGroupListDto[]>>(url, { params });
  }

  getMyDayOffRequestGroups(
    options?: Omit<DayOffGroupQuery, 'scope'>,
  ): Observable<ApiPagedResponse<OfftimeRequestGroupListDto[]>> {
    return this.getDayOffRequestGroups({ ...options, scope: 'mine' });
  }

  getAllDayOffRequestGroups(
    options?: Omit<DayOffGroupQuery, 'scope'>,
  ): Observable<ApiPagedResponse<OfftimeRequestGroupListDto[]>> {
    return this.getDayOffRequestGroups({ ...options, scope: 'all' });
  }

  getDayOffRequestGroupById(
    groupId: number,
    options?: {
      scope?: OfftimeGroupScope;
      isApproved?: ApprovalFilter;
      isCanceled?: boolean;
    },
  ): Observable<OfftimeRequestGroupDetailsDto> {
    const scope = options?.scope ?? 'mine';

    const params = this.buildParams({
      isApproved: this.toIsApprovedParam(options?.isApproved),
      isCanceled: options?.isCanceled === true ? 'true' : undefined,
    });

    const baseUrl = this.resolveGroupsBaseUrl(scope);

    return this.http
      .get<ApiResponse<OfftimeRequestGroupDetailsDto>>(`${baseUrl}/${groupId}`, { params })
      .pipe(map((res) => res.data));
  }

  getMyDayOffRequestGroupById(
    groupId: number,
    options?: { isApproved?: ApprovalFilter; isCanceled?: boolean },
  ): Observable<OfftimeRequestGroupDetailsDto> {
    return this.getDayOffRequestGroupById(groupId, { ...options, scope: 'mine' });
  }

  getAllDayOffRequestGroupById(
    groupId: number,
    options?: { isApproved?: ApprovalFilter; isCanceled?: boolean },
  ): Observable<OfftimeRequestGroupDetailsDto> {
    return this.getDayOffRequestGroupById(groupId, { ...options, scope: 'all' });
  }

  approveDayOffRequestGroup(groupId: number): Observable<void> {
    return this.http
      .patch<ApiResponse<unknown>>(`${this.allGroupsUrl}/${groupId}/approve`, {})
      .pipe(map(() => void 0));
  }

  rejectDayOffRequestGroup(groupId: number): Observable<void> {
    return this.http
      .patch<ApiResponse<unknown>>(`${this.allGroupsUrl}/${groupId}/reject`, {})
      .pipe(map(() => void 0));
  }

  createDayOffRequestGroup(body: CreateDayOffRequestBody): Observable<number> {
    return this.http
      .post<ApiResponse<number>>(`${this.baseUrl}/api/day-off-request-groups`, body)
      .pipe(map((res) => res.data));
  }

  patchDayOffRequestDay(dayOffRequestId: number, body: PatchDayOffRequestBody): Observable<void> {
    return this.http
      .patch<ApiResponse<unknown>>(`${this.baseUrl}/api/day-off-requests/${dayOffRequestId}`, body)
      .pipe(map(() => void 0));
  }

  cancelDayOffRequestDay(dayOffRequestId: number): Observable<void> {
    return this.http
      .patch<ApiResponse<unknown>>(`${this.baseUrl}/api/day-off-requests/${dayOffRequestId}/cancel`, {})
      .pipe(map(() => void 0));
  }

  cancelDayOffRequestGroup(groupId: number): Observable<void> {
    return this.http
      .patch<ApiResponse<unknown>>(`${this.baseUrl}/api/day-off-request-groups/${groupId}/cancel`, {})
      .pipe(map(() => void 0));
  }

  addMoreRequestsToGroup(groupId: number, body: AddMoreDayOffRequestsBody): Observable<void> {
    return this.http
      .patch<ApiResponse<unknown>>(
        `${this.baseUrl}/api/day-off-request-groups/${groupId}/add-more-requests`,
        body,
      )
      .pipe(map(() => void 0));
  }

  addFilesToDayOffRequestGroup(groupId: number, files: File[]): Observable<void> {
    const formData = new FormData();

    for (const file of files) {
      formData.append('files', file);
    }

    return this.http
      .patch<ApiResponse<unknown>>(
        `${this.baseUrl}/api/day-off-request-groups/${groupId}/add-files`,
        formData,
      )
      .pipe(map(() => void 0));
  }
}